import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'

export const useAppStore = defineStore('app', () => {
    // State
    const isLoading = ref(false)
    const isMobile = ref(false)
    const theme = ref('light')
    const sidebarOpen = ref(false)
    const notifications = ref([])
    const confirmDialog = ref({
        show: false,
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null
    })

    const toast = useToast()

    // Getters
    const unreadNotifications = computed(() => {
        return notifications.value.filter(n => !n.read).length
    })

    const isDarkMode = computed(() => theme.value === 'dark')

    // Actions
    const setLoading = (value) => {
        isLoading.value = value
    }

    const setIsMobile = (value) => {
        isMobile.value = value
        // Chiudi sidebar su mobile quando cambia viewport
        if (value) {
            sidebarOpen.value = false
        }
    }

    const setTheme = (newTheme) => {
        theme.value = newTheme
    }

    const toggleTheme = () => {
        theme.value = theme.value === 'light' ? 'dark' : 'light'
    }

    const toggleSidebar = () => {
        sidebarOpen.value = !sidebarOpen.value
    }

    const closeSidebar = () => {
        sidebarOpen.value = false
    }


    // Notifiche
    const addNotification = (notification) => {
        const newNotification = {
            id: Date.now(),
            ...notification,
            timestamp: new Date().toISOString(),
            read: false
        }

        notifications.value.unshift(newNotification)

        // Limita a 50 notifiche
        if (notifications.value.length > 50) {
            notifications.value = notifications.value.slice(0, 50)
        }

        // Mostra toast per notifiche ad alta priorità
        if (notification.priority === 'high') {
            toast.warning(notification.message)
        }

        // Salva in localStorage
        saveNotifications()
    }

    const markNotificationAsRead = (id) => {
        const notification = notifications.value.find(n => n.id === id)
        if (notification) {
            notification.read = true
            saveNotifications()
        }
    }

    const markAllNotificationsAsRead = () => {
        notifications.value.forEach(n => {
            n.read = true
        })
        saveNotifications()
    }

    const clearNotifications = () => {
        notifications.value = []
        localStorage.removeItem('notifications')
    }

    const saveNotifications = () => {
        localStorage.setItem('notifications', JSON.stringify(notifications.value))
    }

    const loadNotifications = () => {
        const saved = localStorage.getItem('notifications')
        if (saved) {
            try {
                notifications.value = JSON.parse(saved)
            } catch (error) {
                console.error('Error loading notifications:', error)
                notifications.value = []
            }
        }
    }

    // Dialog di conferma
    const showConfirm = (options) => {
        return new Promise((resolve) => {
            confirmDialog.value = {
                show: true,
                title: options.title || 'Conferma',
                message: options.message || 'Sei sicuro?',
                confirmText: options.confirmText || 'Conferma',
                cancelText: options.cancelText || 'Annulla',
                onConfirm: () => {
                    confirmDialog.value.show = false
                    resolve(true)
                },
                onCancel: () => {
                    confirmDialog.value.show = false
                    resolve(false)
                }
            }
        })
    }

    const hideConfirm = () => {
        confirmDialog.value.show = false
    }

    // Error handling
    const showError = (message, duration = 5000) => {
        toast.error(message, { timeout: duration })
    }

    const showSuccess = (message, duration = 3000) => {
        toast.success(message, { timeout: duration })
    }

    const showInfo = (message, duration = 3000) => {
        toast.info(message, { timeout: duration })
    }

    // Utility per formattazione
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR'
        }).format(value)
    }

    const formatDate = (date) => {
        return new Intl.DateTimeFormat('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(new Date(date))
    }

    const formatTime = (date) => {
        return new Intl.DateTimeFormat('it-IT', {
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(date))
    }

    const formatDateTime = (date) => {
        return `${formatDate(date)} ${formatTime(date)}`
    }

    // Inizializzazione
    loadNotifications()

    return {
        // State
        isLoading,
        isMobile,
        theme,
        sidebarOpen,
        notifications,
        confirmDialog,

        // Getters
        unreadNotifications,
        isDarkMode,

        // Actions
        setLoading,
        setIsMobile,
        setTheme,
        toggleTheme,
        toggleSidebar,
        closeSidebar,
        addNotification,
        markNotificationAsRead,
        markAllNotificationsAsRead,
        clearNotifications,
        showConfirm,
        hideConfirm,
        showError,
        showSuccess,
        showInfo,
        formatCurrency,
        formatDate,
        formatTime,
        formatDateTime
    }
}, {
    persist: {
        paths: ['theme', 'notifications']
    }
})
